import { useContext } from 'react'
import { CloudOff, ExternalLink, Globe } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { Small } from '@/components/ui/typography'
import { AuthContext } from '../modules/auth/authContext'

const RETOOL_SHOP_UPDATER_URL =
    'https://tieconfig.retool.com/apps/45f95896-6755-11f0-b9a8-5f24e8b6aa8d/SiteConfigPortal/ShopUpdater/ShopUpdater'

const BANNERS = {
    PUBLIC_CATALOG: {
        variant: 'warning',
        Icon: Globe,
        title: 'Public catalog sync',
        description: (clientName: string) =>
            `${clientName} is served from the store's public catalog; the Black Crow Shopify app isn't installed.`,
        actionUrl: undefined,
    },
    DISABLED: {
        variant: 'destructive',
        Icon: CloudOff,
        title: 'Shopify sync disabled',
        description: (clientName: string) =>
            `${clientName} isn't syncing Shopify data; product data may be missing or stale.`,
        actionUrl: RETOOL_SHOP_UPDATER_URL,
    },
} as const

export const useShopifySyncBanner = () => {
    const { client, isSuperUser } = useContext(AuthContext)
    const syncState = client?.shopifySyncState
    if (!isSuperUser || !syncState || syncState === 'SYNCED') {
        return null
    }
    return syncState
}

export const ShopifySyncBanner: React.FunctionComponent = () => {
    const { client } = useContext(AuthContext)
    const syncState = useShopifySyncBanner()
    if (!syncState) {
        return null
    }
    const { variant, Icon, title, description, actionUrl } = BANNERS[syncState]
    const text = description(
        client?.displayName || client?.name || 'This client',
    )
    return (
        <div className="fixed inset-x-0 top-0 z-50 bg-background">
            <Alert
                variant={variant}
                className="flex h-12 items-center justify-center rounded-none border-x-0 border-t-0 py-0"
            >
                <div className="flex min-w-0 items-center gap-3">
                    <Icon className="size-4 shrink-0" />
                    <AlertTitle className="mb-0 whitespace-nowrap">
                        {title}
                    </AlertTitle>
                    <AlertDescription className="truncate" title={text}>
                        {text}
                    </AlertDescription>
                    {actionUrl && (
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="size-7 shrink-0"
                                        asChild
                                    >
                                        <a
                                            href={actionUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <ExternalLink className="size-3.5" />
                                        </a>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <Small className="text-white">
                                        Enable sync in Retool
                                    </Small>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    <Badge variant="secondary" className="shrink-0">
                        Internal
                    </Badge>
                </div>
            </Alert>
        </div>
    )
}
